/**
  * @class mmodSkPatrolControl
  * @description
  *
  * # Ucel
  * Modul v mOffice pre vzdialene ovladanie mPatrol zariadeni.
  * Zobrazuje modal s tlacidlami pre kazdu akciu (restart, exit, logoff, ...).
  * Kazde tlacidlo posle prikaz cez msvrServer na cielovy mPatrol.
  *
  * Modul sa otvara z workflow akcie PATROL_CONTROL v zozname aplikacii.
  *
  */

import mmodModule from 'mmodModule'

export default class mmodSkPatrolControl extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Vytvori instanciu triedy.
  * @param {string} [id] - ID pod ktorym objekt pocuva spravy
  * @param {string} [cfg] - pociatocna konfiguracia objektu
  * @param {string} [cfg_schema] - doplnkove schema vlastnosti z odvodenych tried
  * @param {string} [dependencies] - doplnkove zavislosti z odvodenych tried
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: {
          type: 'float',
          x: 0,
          y: 0,
          width: '100%',
          height: '100%',
          order: 9999,
        },
        description: 'Nastavenie okna - float overlay nad vsetkym',
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Inicializacia GUI komponentov.
  */
  initWindow() {
    this.el.hidden = true
    this._registerTranslations()
    this.mmStart()
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Registruje preklady pre modal.
  * Preklady su definovane priamo v module, nie v globalnom lang.csv,
  * pretoze tento modul je SK-specificky a nema vlastne CSV.
  */
  _registerTranslations() {
    const translations = {
      'Patrol Control': { en: 'Patrol Control', sk: 'Ovládanie patrolu' },
      'App': { en: 'App', sk: 'Aplikácia' },
      'Device': { en: 'Device', sk: 'Zariadenie' },
      'Special': { en: 'Special', sk: 'Špeciálne' },
      'Restart App': { en: 'Restart App', sk: 'Reštart aplikácie' },
      'Exit App': { en: 'Exit App', sk: 'Ukončiť aplikáciu' },
      'Logoff User': { en: 'Logoff User', sk: 'Odhlásiť používateľa' },
      'Restart Device': { en: 'Restart Device', sk: 'Reštart zariadenia' },
      'Shutdown Device': { en: 'Shutdown Device', sk: 'Vypnúť zariadenie' },
      'Security Mode': { en: 'Security Mode', sk: 'Bezpečnostný mód' },
      'Service Mode': { en: 'Service Mode', sk: 'Servisný mód' },
    }
    for(const key in translations) {
      LANG.createTranslation(key, translations[key])
    }
  } // _registerTranslations

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Otvori modal s tlacidlami pre ovladanie cieloveho mPatrol.
  * @param {object} sender - odosielatel spravy
  * @param {object} params - parametre spravy
  * @param {string} params.id - app_id cieloveho mPatrol zariadenia
  */
  mmOpen(sender, params) {
    this._targetId = params?.id
    if(!this._targetId) return

    this.el.hidden = false

    const modal = this.el.modalOpen({
      key: 'patrol_control',
      class: 'patrol-control-modal',
      position: 'center',
      outclick: ':modal.cancel',
      header: `{{Patrol Control}}: ${this._targetId}`,
      items: [
        { tag: 'div', class: 'patrol-control-grid', children: [
          { tag: 'div', class: 'patrol-control-column', children: [
            { tag: 'div', class: 'patrol-control-group-label', children: [LANG.translate(this.el.language, 'App')] },
            { tag: 'mele-button', key: 'restart', label: '{{Restart App}}', color: 'green' },
            { tag: 'mele-button', key: 'exit', label: '{{Exit App}}', color: 'red' },
          ]},
          { tag: 'div', class: 'patrol-control-column', children: [
            { tag: 'div', class: 'patrol-control-group-label', children: [LANG.translate(this.el.language, 'Device')] },
            { tag: 'mele-button', key: 'logoff', label: '{{Logoff User}}', color: 'blue' },
            { tag: 'mele-button', key: 'device_restart', label: '{{Restart Device}}', color: 'orange' },
            { tag: 'mele-button', key: 'device_shutdown', label: '{{Shutdown Device}}', color: 'red' },
          ]},
          { tag: 'div', class: 'patrol-control-column', children: [
            { tag: 'div', class: 'patrol-control-group-label', children: [LANG.translate(this.el.language, 'Special')] },
            { tag: 'mele-button', key: 'security', label: '{{Security Mode}}', color: 'red' },
            { tag: 'mele-button', key: 'service', label: '{{Service Mode}}', color: 'orange' },
          ]},
        ]},
      ],
      footerbuttons: ['cancel'],
    })

    modal.eventCancel = () => {
      this.close()
    }
  } // mmOpen

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Zatvori modal a skryje modul.
  */
  close() {
    this.el.modalClose('patrol_control')
    this.el.hidden = true
    this._targetId = null
  } // close

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Handler pre kliknutie na tlacidlo v modali.
  * @param {object} sender - odosielatel spravy
  * @param {object} params - parametre spravy
  * @param {string} params.button - key tlacidla (= command)
  */
  mmButtonClicked(sender, params) {
    const command = params.button
    const commands = ['restart', 'exit', 'logoff', 'device_restart', 'device_shutdown', 'security', 'service']
    if(!commands.includes(command) || !this._targetId) return

    this.publish(':msvrServer', 'mmClientAppControl', { id: this._targetId, command })
    this.close()
  } // mmButtonClicked

} // mmodSkPatrolControl